// app/javascript/controllers/operational_areas_map_controller.js
// Uses global mapboxgl loaded via CDN script tags
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "countryFilter", "styleButton"]
  static values = {
    apiKey: String,
    geojsonUrl: String,
    operationalAreas: Array,
    currentProjectId: Number
  }

  connect() {
    if (!this.hasApiKeyValue) return
    if (!window.mapboxgl || !window.mapboxgl.supported()) {
      this.mapTarget.innerHTML = '<p style="padding: 1rem; text-align: center; color: #666;">La carte interactive n\'est pas disponible sur ce navigateur.</p>'
      return
    }

    window.mapboxgl.accessToken = this.apiKeyValue
    this.initializeMap()
  }

  async initializeMap() {
    // Calculer le centre et les limites des zones opérationnelles
    const bounds = this.calculateBounds()

    // Style par défaut
    this.currentStyle = 'streets'
    this.mapStyles = {
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
    }

    // Désactiver la télémétrie Mapbox (RGPD)
    try { window.mapboxgl.config.EVENTS_URL = '' } catch (e) { /* read-only */ }

    this.map = new window.mapboxgl.Map({
      container: this.mapTarget,
      style: this.mapStyles.streets,
      center: bounds.center,
      zoom: 2,
      collectResourceTiming: false,
      accessToken: this.apiKeyValue,

      // Configuration pour désactiver le globe et forcer la projection plate
      projection: 'mercator',
      renderWorldCopies: false,

      // Limiter les interactions pour éviter les rotations 3D
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false
    })
  
    // Ajouter les contrôles
    this.map.addControl(new window.mapboxgl.NavigationControl(), 'top-right')
    this.map.addControl(new window.mapboxgl.FullscreenControl(), 'top-right')

    // Tracker pour le hover sur polygones
    this.hoveredAdminName = null

    this.map.on('load', () => {
      // Enregistrer l'image du pin custom
      this.registerPinImage()
      this.loadAdministrativeBoundaries()
      this.addOperationalAreas()
      // Ajuster automatiquement la vue après le chargement des zones
      setTimeout(() => this.fitBounds(), 500)
    })
  }

  createCustomPinImage() {
    const size = 80
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')

    const cx = size / 2
    const r = size / 4.5
    const tipY = size - 2
    const topY = 6

    // Ombre portée
    ctx.beginPath()
    ctx.arc(cx + 1, topY + r + 1, r, Math.PI, 0, false)
    ctx.quadraticCurveTo(cx + r + 1, topY + r * 2.2 + 1, cx + 1, tipY + 1)
    ctx.quadraticCurveTo(cx - r + 1, topY + r * 2.2 + 1, cx - r + 1, topY + r + 1)
    ctx.closePath()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)'
    ctx.fill()

    // Forme du pin (goutte)
    ctx.beginPath()
    ctx.arc(cx, topY + r, r, Math.PI, 0, false)
    ctx.quadraticCurveTo(cx + r, topY + r * 2.2, cx, tipY)
    ctx.quadraticCurveTo(cx - r, topY + r * 2.2, cx - r, topY + r)
    ctx.closePath()

    // Dégradé
    const grad = ctx.createLinearGradient(cx - r, topY, cx + r, tipY)
    grad.addColorStop(0, '#60a5fa')
    grad.addColorStop(1, '#2563eb')
    ctx.fillStyle = grad
    ctx.fill()

    // Contour blanc
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2.5
    ctx.stroke()

    // Point lumineux interne
    ctx.beginPath()
    ctx.arc(cx, topY + r, r * 0.38, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
    ctx.fill()

    // Retourner au format attendu par Mapbox GL JS
    const imageData = ctx.getImageData(0, 0, size, size)
    return { width: size, height: size, data: new Uint8Array(imageData.data.buffer) }
  }

  registerPinImage() {
    if (!this.map.hasImage('custom-pin')) {
      this.map.addImage('custom-pin', this.createCustomPinImage(), { pixelRatio: 2 })
    }
  }

  calculateBounds() {
    if (!this.operationalAreasValue || this.operationalAreasValue.length === 0) {
      return {
        center: [-72.285215, 18.971187],
        zoom: 5
      }
    }
  
    const lats = this.operationalAreasValue.map(area => area.latitude)
    const lngs = this.operationalAreasValue.map(area => area.longitude)
  
    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    }
  
    // Calculer le centre
    const centerLat = (bounds.north + bounds.south) / 2
    const centerLng = (bounds.east + bounds.west) / 2
  
    // Ajouter une marge aux limites pour que tous les points soient visibles
    const latMargin = Math.max((bounds.north - bounds.south) * 0.1, 0.1)
    const lngMargin = Math.max((bounds.east - bounds.west) * 0.1, 0.1)
  
    return {
      center: [centerLng, centerLat],
      zoom: 6, // Zoom par défaut plus conservateur
      bounds: [
        [bounds.west - lngMargin, bounds.south - latMargin], 
        [bounds.east + lngMargin, bounds.north + latMargin]
      ]
    }
  }

  async loadAdministrativeBoundaries() {
    if (!this.hasGeojsonUrlValue) return

    try {
      // Utiliser l'endpoint arko_admin qui retourne le GeoJSON avec les géométries polygonales
      const response = await fetch(this.geojsonUrlValue)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const geojsonData = await response.json()

      // Filtrer pour ne garder que les pays ayant des zones opérationnelles
      const relevantCountries = new Set(
        this.operationalAreasValue.map(area => area.admin0?.name).filter(Boolean)
      )
      if (relevantCountries.size > 0) {
        geojsonData.features = geojsonData.features.filter(f =>
          relevantCountries.has(f.properties.name)
        )
      }

      // Ajouter la source pour les frontières administratives
      this.map.addSource('admin-boundaries', {
        type: 'geojson',
        data: geojsonData
      })

      // Styles pour les différents niveaux administratifs
      this.addAdminBoundaryLayers()

    } catch (error) {
    }
  }

  addAdminBoundaryLayers() {
    // Couches pour les frontières administratives (initialement visibles)
    // admins.geojson contient des pays (Admin-0) avec la propriété 'name'
    this.map.addLayer({
      id: 'admin1-fill',
      type: 'fill',
      source: 'admin-boundaries',
      filter: ['has', 'name'],
      paint: {
        'fill-color': '#e3f2fd',
        'fill-opacity': 0.1
      }
    })

    this.map.addLayer({
      id: 'admin1-boundaries',
      type: 'line',
      source: 'admin-boundaries',
      filter: ['has', 'name'],
      paint: {
        'line-color': '#1976d2',
        'line-width': 1,
        'line-opacity': 0.4
      }
    })

    // Couche de surbrillance au hover (invisible par défaut)
    this.map.addLayer({
      id: 'admin1-fill-hover',
      type: 'fill',
      source: 'admin-boundaries',
      filter: ['==', 'name', ''],
      paint: {
        'fill-color': '#1976d2',
        'fill-opacity': 0.25
      }
    })

    this.map.addLayer({
      id: 'admin1-boundaries-hover',
      type: 'line',
      source: 'admin-boundaries',
      filter: ['==', 'name', ''],
      paint: {
        'line-color': '#0d47a1',
        'line-width': 3,
        'line-opacity': 0.9
      }
    })
  }

  addOperationalAreas() {
    if (!this.operationalAreasValue || this.operationalAreasValue.length === 0) return

    // Créer les données GeoJSON pour les zones opérationnelles
    const geojsonData = {
      type: "FeatureCollection",
      features: this.operationalAreasValue.map(area => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [area.longitude, area.latitude]
        },
        properties: {
          id: area.id,
          admin0: area.admin0?.name || 'Inconnu',
          admin1: area.admin1?.name || 'Inconnu',
          admin2: area.admin2?.name || null,
          created_by: area.created_by?.first_name + ' ' + area.created_by?.last_name,
          created_at: area.created_at,
          admin0_id: area.admin0?.id,
          project_title: area.operationalable?.title || area.project?.title || null,
          project_id: area.operationalable?.id || area.project?.id || null,
          project_type: area.operationalable_type || null,
          project_url: area.operationalable?.id ? `/projects/${area.operationalable.id}` : (area.project?.id ? `/projects/${area.project.id}` : null)
        }
      }))
    }

    // Ajouter la source des zones opérationnelles
    this.map.addSource('operational-areas', {
      type: 'geojson',
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Couches de marqueurs
    this.addClusterLayers()
    this.addUnclusteredPointLayer(['!', ['has', 'point_count']])

    // Interactions
    this.addMapInteractions()
  }

  addClusterLayers() {
    // Halo (anneau semi-transparent derrière le cluster)
    this.map.addLayer({
      id: 'cluster-halo',
      type: 'circle',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#3b82f6',
          10,
          '#f59e0b',
          30,
          '#ef4444'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          26,
          10,
          36,
          30,
          46
        ],
        'circle-opacity': 0.2
      }
    })

    // Cluster principal
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#3b82f6',
          10,
          '#f59e0b',
          30,
          '#ef4444'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          18,
          10,
          24,
          30,
          32
        ],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff'
      }
    })

    // Label du cluster
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 13
      },
      paint: {
        'text-color': '#ffffff'
      }
    })
  }

  addUnclusteredPointLayer(filter) {
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'operational-areas',
      filter: filter || null,
      layout: {
        'icon-image': 'custom-pin',
        'icon-size': 1,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true
      }
    })
  }

  // Stratégie 1 : Décider si on zoom ou affiche un popup
  getClusterAction(clusterFeatures, currentZoom, pointCount) {
    const maxZoom = 14           // Zoom maximum avant popup forcé
    const maxPopupSize = 10      // Taille maximale pour afficher popup

    // 1. Zoom max atteint → popup forcé
    if (currentZoom >= maxZoom) {
      return 'popup'
    }

    // 2. Trop de zones → zoom obligatoire
    if (pointCount > maxPopupSize) {
      return 'zoom'
    }

    // 3. Analyser la diversité géographique
    const countries = new Set()
    const regions = new Set()

    clusterFeatures.forEach(feature => {
      const props = feature.properties
      countries.add(props.admin0)
      regions.add(`${props.admin0}:${props.admin1}`) // Combinaison pays:région
    })

    // Plusieurs pays → zoom pour séparer
    if (countries.size > 1) {
      return 'zoom'
    }

    // Même pays mais plusieurs régions → zoom pour séparer
    if (regions.size > 1) {
      return 'zoom'
    }

    // Même région + taille raisonnable → popup
    return 'popup'
  }

  createClusterPopupContent(features, count) {
    // Extraire les informations de région (prendre la première comme référence)
    const firstFeature = features[0].properties
    const region = firstFeature.admin1
    const country = firstFeature.admin0

    // Extraire les projets uniques avec leurs infos
    const projectsMap = new Map()

    features.forEach(feature => {
      const props = feature.properties
      if (props.project_id && props.project_title) {
        if (!projectsMap.has(props.project_id)) {
          projectsMap.set(props.project_id, {
            id: props.project_id,
            title: props.project_title,
            url: props.project_url,
            zones: 0
          })
        }
        projectsMap.get(props.project_id).zones++
      }
    })

    const projects = Array.from(projectsMap.values())
    const hasProjects = projects.length > 0

    // Design élégant et compact
    return `
      <div class="min-w-[280px] max-w-[320px]">
        <!-- Header avec région et badge -->
        <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center mb-1">
                <svg class="w-4 h-4 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <h3 class="font-semibold text-sm">${region}</h3>
              </div>
              <p class="text-xs opacity-90">${country}</p>
            </div>
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 backdrop-blur-sm mr-4">
              ${count} zone${count > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <!-- Corps du popup -->
        <div class="bg-white px-4 py-3 ">
          ${hasProjects ? `
            <div class="space-y-2 max-h-60 overflow-y-auto">
              <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                ${projects.length} Projet${projects.length > 1 ? 's' : ''}
              </p>
              ${projects.map(project => `
                <a href="${project.url}"
                   class="block group hover:bg-blue-50 -mx-2 px-2 py-2 rounded-lg transition-colors duration-150">
                  <div class="flex items-start">
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        ${project.title}
                      </p>
                      ${project.zones > 1 ? `
                        <p class="text-xs text-gray-500 mt-0.5">
                          ${project.zones} zones d'intervention
                        </p>
                      ` : ''}
                    </div>
                    <svg class="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </a>
              `).join('')}
            </div>
          ` : `
            <div class="text-center py-3">
              <svg class="w-8 h-8 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
              </svg>
              <p class="text-sm text-gray-500">Zones sans projet associé</p>
            </div>
          `}
        </div>
      </div>
    `
  }

  addMapInteractions() {
    // Clic sur un cluster - Stratégie 1 : Zoom par région hiérarchique
    this.map.on('click', 'clusters', async (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })
      const clusterId = features[0].properties.cluster_id
      const pointCount = features[0].properties.point_count
      const coordinates = features[0].geometry.coordinates.slice()
      const currentZoom = this.map.getZoom()

      // Récupérer tous les points du cluster
      this.map.getSource('operational-areas').getClusterLeaves(
        clusterId,
        pointCount,
        0,
        (err, clusterFeatures) => {
          if (err) {
            return
          }

          // Décider : zoom ou popup ?
          const action = this.getClusterAction(clusterFeatures, currentZoom, pointCount)

          if (action === 'popup') {
            // Afficher la popup avec la liste des zones
            const popupContent = this.createClusterPopupContent(clusterFeatures, pointCount)

            // Assurer que la popup s'affiche dans les limites de la carte
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
              coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
            }

            new window.mapboxgl.Popup({
              maxWidth: '340px',
              className: 'cluster-popup',
              closeButton: true,
              closeOnClick: false,
              offset: 25,  // Offset pour éviter que la popup chevauche le cluster
              anchor: 'bottom'  // Ancre en bas pour que la popup pointe vers le cluster
            })
              .setLngLat(coordinates)
              .setHTML(popupContent)
              .addTo(this.map)
          } else {
            // Zoomer vers le cluster
            this.map.getSource('operational-areas').getClusterExpansionZoom(
              clusterId,
              (err, zoom) => {
                if (err) return

                this.map.easeTo({
                  center: coordinates,
                  zoom: zoom,
                  duration: 1000
                })
              }
            )
          }
        }
      )
    })

    // Clic sur un point individuel
    this.map.on('click', 'unclustered-point', (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice()
      const clickedProperties = e.features[0].properties

      // Chercher d'autres points aux mêmes coordonnées (ou très proches - rayon de 0.0001°)
      const nearbyFeatures = this.operationalAreasValue.filter(area => {
        const latDiff = Math.abs(area.latitude - coordinates[1])
        const lngDiff = Math.abs(area.longitude - coordinates[0])
        return latDiff < 0.0001 && lngDiff < 0.0001
      })

      let popupContent

      if (nearbyFeatures.length > 1) {
        // Plusieurs zones aux mêmes coordonnées - afficher une liste
        popupContent = `
          <div class="p-4">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-semibold text-gray-900 text-base">Zones opérationnelles</h3>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                ${nearbyFeatures.length} zones superposées
              </span>
            </div>
            <div class="space-y-2 max-h-80 overflow-y-auto">
              ${nearbyFeatures.map((area, index) => {
                const projectTitle = area.operationalable?.title || area.project?.title
                const projectId = area.operationalable?.id || area.project?.id
                const projectUrl = projectId ? `/projects/${projectId}` : null
                return `
                  <div class="border-l-4 ${index === 0 ? 'border-orange-500' : 'border-gray-300'} pl-3 py-2 bg-gray-50 rounded-r">
                    ${projectTitle ? `
                      <div class="mb-2">
                        ${projectUrl ? `
                          <a href="${projectUrl}" class="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">${projectTitle}</a>
                        ` : `
                          <p class="text-sm font-medium text-blue-600">${projectTitle}</p>
                        `}
                      </div>
                    ` : ''}
                    <div class="text-sm space-y-1">
                      <p class="font-medium text-gray-900">${area.admin0?.name || 'Inconnu'}</p>
                      <p class="text-gray-600">${area.admin1?.name || 'Inconnu'}</p>
                      ${area.admin2?.name ? `<p class="text-gray-500 text-xs">${area.admin2.name}</p>` : ''}
                    </div>
                  </div>
                `
              }).join('')}
            </div>
            <div class="mt-3 pt-3 border-t border-gray-200">
              <p class="text-xs text-gray-400">
                <strong>Coordonnées :</strong> ${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}
              </p>
            </div>
          </div>
        `
      } else {
        // Une seule zone - afficher le projet en avant
        const area = nearbyFeatures[0]
        const projectTitle = area?.operationalable?.title || area?.project?.title
        const projectId = area?.operationalable?.id || area?.project?.id
        const projectUrl = projectId ? `/projects/${projectId}` : null

        // Vérifier si on est déjà sur la page de ce projet
        const isCurrentProject = this.hasCurrentProjectIdValue && projectId === this.currentProjectIdValue

        if (projectTitle && projectUrl && !isCurrentProject) {
          // Projet disponible et différent - design harmonisé avec cluster
          popupContent = `
            <div class="min-w-[280px] max-w-[320px]">
              <!-- Header avec région et badge -->
              <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
                <div class="flex items-start justify-between">
                  <div class="flex-1">
                    <div class="flex items-center mb-1">
                      <svg class="w-4 h-4 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <h3 class="font-semibold text-sm">${clickedProperties.admin1}</h3>
                    </div>
                    <p class="text-xs opacity-90">${clickedProperties.admin0}</p>
                    ${clickedProperties.admin2 ? `<p class="text-xs opacity-75 mt-0.5">${clickedProperties.admin2}</p>` : ''}
                  </div>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 backdrop-blur-sm mr-4">
                    1 zone
                  </span>
                </div>
              </div>

              <!-- Corps du popup avec projet -->
              <div class="bg-white px-4 py-3 ">
                <a href="${projectUrl}"
                   class="block group hover:bg-blue-50 -mx-2 px-2 py-3 rounded-lg transition-colors duration-150">
                  <div class="flex items-start">
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        ${projectTitle}
                      </p>
                      <p class="text-xs text-gray-500 mt-1">
                        Cliquer pour voir le projet
                      </p>
                    </div>
                    <svg class="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          `
        } else if (projectTitle && isCurrentProject) {
          // C'est le projet actuel - design compact
          popupContent = `
            <div class="min-w-[240px] max-w-[300px]">
              <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
                <div class="flex items-center mb-1">
                  <svg class="w-3.5 h-3.5 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <h3 class="font-semibold text-sm">${clickedProperties.admin1}</h3>
                </div>
                <p class="text-xs opacity-80">${clickedProperties.admin0}</p>
                ${clickedProperties.admin2 ? `<p class="text-xs opacity-70 mt-0.5">${clickedProperties.admin2}</p>` : ''}
              </div>
              <div class="bg-white px-4 py-2.5 ">
                <p class="text-sm text-gray-900 font-medium">${projectTitle}</p>
                <div class="flex items-center mt-1 text-xs text-green-600">
                  <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  Projet actuel
                </div>
              </div>
            </div>
          `
        } else {
          // Fallback - utiliser clickedProperties qui contient les vraies données
          const isCurrentProjectFallback = this.hasCurrentProjectIdValue && clickedProperties.project_id === this.currentProjectIdValue

          if (clickedProperties.project_title && clickedProperties.project_url && !isCurrentProjectFallback) {
            // Projet disponible via clickedProperties - design harmonisé
            popupContent = `
              <div class="min-w-[280px] max-w-[320px]">
                <!-- Header avec région et badge -->
                <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-4 h-4 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <h3 class="font-semibold text-sm">${clickedProperties.admin1}</h3>
                      </div>
                      <p class="text-xs opacity-90">${clickedProperties.admin0}</p>
                      ${clickedProperties.admin2 ? `<p class="text-xs opacity-75 mt-0.5">${clickedProperties.admin2}</p>` : ''}
                    </div>
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 backdrop-blur-sm mr-4">
                      1 zone
                    </span>
                  </div>
                </div>

                <!-- Corps du popup avec projet -->
                <div class="bg-white px-4 py-3 ">
                  <a href="${clickedProperties.project_url}"
                     class="block group hover:bg-blue-50 -mx-2 px-2 py-3 rounded-lg transition-colors duration-150">
                    <div class="flex items-start">
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                          ${clickedProperties.project_title}
                        </p>
                      </div>
                      <svg class="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
            `
          } else if (clickedProperties.project_title && isCurrentProjectFallback) {
            // C'est le projet actuel via fallback - design compact
            popupContent = `
              <div class="min-w-[240px] max-w-[300px]">
                <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
                  <div class="flex items-center mb-1">
                    <svg class="w-3.5 h-3.5 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    <h3 class="font-semibold text-sm">${clickedProperties.admin1}</h3>
                  </div>
                  <p class="text-xs opacity-80">${clickedProperties.admin0}</p>
                  ${clickedProperties.admin2 ? `<p class="text-xs opacity-70 mt-0.5">${clickedProperties.admin2}</p>` : ''}
                </div>
                <div class="bg-white px-4 py-2.5 ">
                  <p class="text-sm text-gray-900 font-medium">${clickedProperties.project_title}</p>
                  <div class="flex items-center mt-1 text-xs text-green-600">
                    <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    Projet actuel
                  </div>
                </div>
              </div>
            `
          } else {
            // Vraiment pas de projet - design harmonisé minimal
            popupContent = `
              <div class="min-w-[280px] max-w-[320px]">
                <!-- Header avec région et badge -->
                <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3 ">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <svg class="w-4 h-4 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <h3 class="font-semibold text-sm">${clickedProperties.admin1}</h3>
                      </div>
                      <p class="text-xs opacity-90">${clickedProperties.admin0}</p>
                      ${clickedProperties.admin2 ? `<p class="text-xs opacity-75 mt-0.5">${clickedProperties.admin2}</p>` : ''}
                    </div>
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 backdrop-blur-sm mr-4">
                      1 zone
                    </span>
                  </div>
                </div>

                <!-- Corps du popup - sans projet -->
                <div class="bg-white px-4 py-3 ">
                  <div class="text-center py-2">
                    <svg class="w-6 h-6 mx-auto text-gray-300 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                    </svg>
                    <p class="text-xs text-gray-500">Zone sans projet associé</p>
                  </div>
                </div>
              </div>
            `
          }
        }
      }

      // Assurer que la popup s'affiche dans les limites de la carte
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
      }

      new window.mapboxgl.Popup({
        maxWidth: nearbyFeatures.length > 1 ? '400px' : '300px',
        offset: [0, -36]
      })
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(this.map)
    })

    // Changer le curseur au survol
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })
    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = ''
    })
    this.map.on('mouseenter', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })
    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = ''
    })

    // ==================== INTERACTIONS POLYGONES ====================

    // Hover sur les polygones (pays)
    this.map.on('mousemove', 'admin1-fill', (e) => {
      if (e.features.length > 0) {
        const adminName = e.features[0].properties.name
        if (adminName && this.hoveredAdminName !== adminName) {
          this.hoveredAdminName = adminName
          if (this.map.getLayer('admin1-fill-hover')) {
            this.map.setFilter('admin1-fill-hover', ['==', 'name', adminName])
          }
          if (this.map.getLayer('admin1-boundaries-hover')) {
            this.map.setFilter('admin1-boundaries-hover', ['==', 'name', adminName])
          }
        }
        this.map.getCanvas().style.cursor = 'pointer'
      }
    })

    this.map.on('mouseleave', 'admin1-fill', () => {
      this.hoveredAdminName = null
      if (this.map.getLayer('admin1-fill-hover')) {
        this.map.setFilter('admin1-fill-hover', ['==', 'name', ''])
      }
      if (this.map.getLayer('admin1-boundaries-hover')) {
        this.map.setFilter('admin1-boundaries-hover', ['==', 'name', ''])
      }
      this.map.getCanvas().style.cursor = ''
    })

    // Click sur un polygone (pays) pour afficher les projets
    this.map.on('click', 'admin1-fill', (e) => {
      if (e.features.length === 0) return

      const feature = e.features[0]
      const adminName = feature.properties.name
      if (!adminName) return
      const continent = feature.properties.continent || ''
      const coordinates = e.lngLat

      // Chercher les zones opérationnelles dans ce pays
      const matchingAreas = this.operationalAreasValue.filter(area =>
        area.admin0?.name === adminName
      )

      // Ne pas afficher de popup si aucun projet dans ce pays
      if (matchingAreas.length === 0) return

      const popupContent = this.createPolygonPopupContent(adminName, matchingAreas, continent)

      new window.mapboxgl.Popup({
        maxWidth: '340px',
        closeButton: true,
        closeOnClick: true
      })
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(this.map)
    })
  }

  createPolygonPopupContent(adminName, matchingAreas, continent) {
    // Extraire les projets uniques
    const projectsMap = new Map()
    matchingAreas.forEach(area => {
      const projectId = area.operationalable?.id || area.project?.id
      const projectTitle = area.operationalable?.title || area.project?.title
      if (projectId && projectTitle) {
        if (!projectsMap.has(projectId)) {
          projectsMap.set(projectId, {
            id: projectId,
            title: projectTitle,
            url: `/projects/${projectId}`,
            zones: 0
          })
        }
        projectsMap.get(projectId).zones++
      }
    })

    const projects = Array.from(projectsMap.values())

    return `
      <div class="min-w-[280px] max-w-[320px]">
        <div class="bg-gradient-to-r from-gray-800 to-blue-600 text-white px-4 py-3">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center mb-1">
                <svg class="w-4 h-4 mr-1.5 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                </svg>
                <h3 class="font-semibold text-sm">${adminName}</h3>
              </div>
              ${continent ? `<p class="text-xs opacity-90">${continent}</p>` : ''}
            </div>
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 backdrop-blur-sm mr-4">
              ${matchingAreas.length} zone${matchingAreas.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div class="bg-white px-4 py-3">
          <div class="space-y-2 max-h-60 overflow-y-auto">
            <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              ${projects.length} Projet${projects.length > 1 ? 's' : ''}
            </p>
            ${projects.map(project => `
              <a href="${project.url}"
                 class="block group hover:bg-blue-50 -mx-2 px-2 py-2 rounded-lg transition-colors duration-150">
                <div class="flex items-start">
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      ${project.title}
                    </p>
                    ${project.zones > 1 ? `
                      <p class="text-xs text-gray-500 mt-0.5">
                        ${project.zones} zones d'intervention
                      </p>
                    ` : ''}
                  </div>
                  <svg class="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              </a>
            `).join('')}
          </div>
        </div>
      </div>
    `
  }

  // Actions déclenchées par l'interface
  toggleMapStyle() {
    // Basculer entre les styles
    this.currentStyle = this.currentStyle === 'streets' ? 'satellite' : 'streets'

    // Mettre à jour le bouton
    this.updateStyleButton()

    // Sauvegarder l'état actuel (sources et layers personnalisées)
    const adminBoundariesData = this.map.getSource('admin-boundaries')?._data
    const operationalAreasData = this.map.getSource('operational-areas')?._data
    const currentCenter = this.map.getCenter()
    const currentZoom = this.map.getZoom()

    // Changer le style de la carte (diff: false pour éviter les erreurs de sprite)
    this.map.setStyle(this.mapStyles[this.currentStyle], { diff: false })

    // Attendre que le nouveau style soit chargé
    this.map.once('style.load', () => {
      // Re-enregistrer l'image du pin custom (perdue au changement de style)
      this.registerPinImage()

      // Restaurer les sources personnalisées
      if (adminBoundariesData) {
        this.map.addSource('admin-boundaries', {
          type: 'geojson',
          data: adminBoundariesData
        })
        this.addAdminBoundaryLayers()
      }

      if (operationalAreasData) {
        const clusterCheckbox = document.getElementById('cluster-zones')
        const enableClustering = clusterCheckbox ? clusterCheckbox.checked : true

        this.map.addSource('operational-areas', {
          type: 'geojson',
          data: operationalAreasData,
          cluster: enableClustering,
          clusterMaxZoom: enableClustering ? 14 : null,
          clusterRadius: enableClustering ? 50 : null
        })

        if (enableClustering) {
          this.addClusterLayers()
        }
        this.addUnclusteredPointLayer(enableClustering ? ['!', ['has', 'point_count']] : null)

        // Réajouter les interactions
        this.addMapInteractions()
      }

      // Restaurer la position de la carte
      this.map.setCenter(currentCenter)
      this.map.setZoom(currentZoom)
    })
  }

  updateStyleButton() {
    if (!this.hasStyleButtonTarget) return

    const button = this.styleButtonTarget
    const iconSvg = this.currentStyle === 'streets'
      ? `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>`
      : `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
        </svg>`

    const label = this.currentStyle === 'streets' ? 'Satellite' : 'Standard'

    button.innerHTML = `${iconSvg}<span class="text-sm">${label}</span>`
  }

  toggleAdminBoundaries(event) {
    const visible = event.target.checked
    const visibility = visible ? 'visible' : 'none'

    if (this.map.getLayer('admin1-fill')) {
      this.map.setLayoutProperty('admin1-fill', 'visibility', visibility)
    }
    if (this.map.getLayer('admin1-boundaries')) {
      this.map.setLayoutProperty('admin1-boundaries', 'visibility', visibility)
    }
    if (this.map.getLayer('admin1-fill-hover')) {
      this.map.setLayoutProperty('admin1-fill-hover', 'visibility', visibility)
    }
    if (this.map.getLayer('admin1-boundaries-hover')) {
      this.map.setLayoutProperty('admin1-boundaries-hover', 'visibility', visibility)
    }
  }

  toggleClustering(event) {
    const enableClustering = event.target.checked

    // Supprimer les layers et la source existante
    if (this.map.getSource('operational-areas')) {
      if (this.map.getLayer('cluster-halo')) this.map.removeLayer('cluster-halo')
      if (this.map.getLayer('clusters')) this.map.removeLayer('clusters')
      if (this.map.getLayer('cluster-count')) this.map.removeLayer('cluster-count')
      if (this.map.getLayer('unclustered-point')) this.map.removeLayer('unclustered-point')
      this.map.removeSource('operational-areas')
    }

    // Recréer avec ou sans clustering
    const geojsonData = {
      type: "FeatureCollection",
      features: this.operationalAreasValue.map(area => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [area.longitude, area.latitude]
        },
        properties: {
          id: area.id,
          admin0: area.admin0?.name || 'Inconnu',
          admin1: area.admin1?.name || 'Inconnu',
          admin2: area.admin2?.name || null,
          created_by: area.created_by?.first_name + ' ' + area.created_by?.last_name,
          created_at: area.created_at,
          admin0_id: area.admin0?.id,
          project_title: area.operationalable?.title || area.project?.title || null,
          project_id: area.operationalable?.id || area.project?.id || null,
          project_type: area.operationalable_type || null,
          project_url: area.operationalable?.id ? `/projects/${area.operationalable.id}` : (area.project?.id ? `/projects/${area.project.id}` : null)
        }
      }))
    }

    this.map.addSource('operational-areas', {
      type: 'geojson',
      data: geojsonData,
      cluster: enableClustering,
      clusterMaxZoom: enableClustering ? 14 : null,
      clusterRadius: enableClustering ? 50 : null
    })

    if (enableClustering) {
      this.addClusterLayers()
    }
    this.addUnclusteredPointLayer(enableClustering ? ['!', ['has', 'point_count']] : null)

    // Réajouter les interactions
    this.addMapInteractions()
  }

  fitBounds() {
    if (!this.operationalAreasValue || this.operationalAreasValue.length === 0) return

    const bounds = this.calculateBounds()
    if (bounds.bounds) {
      this.map.fitBounds(bounds.bounds, {
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        maxZoom: 10,
        duration: 1000
      })
    }
  }

  filterByCountry(event) {
    const countryId = event.target.value

    let filteredAreas = this.operationalAreasValue
    if (countryId) {
      filteredAreas = this.operationalAreasValue.filter(area =>
        area.admin0?.id.toString() === countryId
      )
    }

    // Mettre à jour la source avec les données filtrées
    const geojsonData = {
      type: "FeatureCollection",
      features: filteredAreas.map(area => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [area.longitude, area.latitude]
        },
        properties: {
          id: area.id,
          admin0: area.admin0?.name || 'Inconnu',
          admin1: area.admin1?.name || 'Inconnu',
          admin2: area.admin2?.name || null,
          created_by: area.created_by?.first_name + ' ' + area.created_by?.last_name,
          created_at: area.created_at,
          admin0_id: area.admin0?.id,
          project_title: area.operationalable?.title || area.project?.title || null,
          project_id: area.operationalable?.id || area.project?.id || null,
          project_type: area.operationalable_type || null,
          project_url: area.operationalable?.id ? `/projects/${area.operationalable.id}` : (area.project?.id ? `/projects/${area.project.id}` : null)
        }
      }))
    }

    if (this.map.getSource('operational-areas')) {
      this.map.getSource('operational-areas').setData(geojsonData)
    }

    // Ajuster la vue si des zones sont filtrées
    if (filteredAreas.length > 0 && countryId) {
      const lats = filteredAreas.map(area => area.latitude)
      const lngs = filteredAreas.map(area => area.longitude)

      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2

      // Calculer le zoom adapté à l'étendue
      const latSpread = Math.max(...lats) - Math.min(...lats)
      const lngSpread = Math.max(...lngs) - Math.min(...lngs)
      const maxSpread = Math.max(latSpread, lngSpread)
      const zoom = maxSpread > 20 ? 3 : maxSpread > 10 ? 4 : maxSpread > 5 ? 5 : maxSpread > 2 ? 6 : maxSpread > 0.5 ? 8 : 10

      this.map.flyTo({
        center: [centerLng, centerLat],
        zoom: Math.min(zoom, 10),
        duration: 2500,
        essential: true,
        curve: 1.2,
        easing: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
      })
    } else if (!countryId) {
      // Revenir à la vue complète
      const bounds = this.calculateBounds()
      if (bounds.bounds) {
        this.map.flyTo({
          center: [(bounds.bounds[0][0] + bounds.bounds[1][0]) / 2, (bounds.bounds[0][1] + bounds.bounds[1][1]) / 2],
          zoom: 2,
          duration: 2500,
          essential: true,
          curve: 1.2,
          easing: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
        })
      }
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }
}
